import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useId, useRef, useState } from "react";
import { getTeaching } from "@/lib/teachings.functions";
import { getWatchProgress, upsertWatchProgress, getMyProfile } from "@/lib/user.functions";
import {
  listTeachingComments,
  createTeachingComment,
  deleteTeachingComment,
} from "@/lib/comments.functions";
import { supabase } from "@/integrations/supabase/client";
import { TeachingCard } from "@/components/teaching-card";
import { FavoriteButton } from "@/components/favorite-button";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Calendar, Sun, Moon, Book, MessageCircle, Trash2, Loader2 } from "lucide-react";
import { formatDateBR, formatDateTimeBR } from "@/lib/datetime";

export const Route = createFileRoute("/_authenticated/ensinos/$id")({
  loader: async ({ params }) => {
    try {
      return await getTeaching({ data: { id: params.id } });
    } catch {
      return null;
    }
  },
  head: ({ loaderData }) => {
    const t = (loaderData as any)?.teaching;
    const titulo = (t?.custom_title?.trim() || t?.title || "Ensino") as string;
    const pageTitle = `${titulo} · Beit Tefilah Rechovot`;
    const descBase =
      (t?.description as string | undefined)?.replace(/\s+/g, " ").trim().slice(0, 155) ||
      `Assista ao ensino "${titulo}" da congregação Beit Tefilah Rechovot, com data, Parashá e referências bíblicas.`;
    return {
      meta: [
        { title: pageTitle },
        { name: "description", content: descBase },
        { name: "robots", content: "noindex" },
        { property: "og:type", content: "video.other" },
        { property: "og:title", content: pageTitle },
        { property: "og:description", content: descBase },
        { name: "twitter:title", content: pageTitle },
        { name: "twitter:description", content: descBase },
      ],
      scripts: t
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "VideoObject",
                name: titulo,
                headline: titulo,
                description: descBase,
                uploadDate: t.published_at ?? t.teaching_date ?? undefined,
                thumbnailUrl: t.youtube_video_id
                  ? `https://i.ytimg.com/vi/${t.youtube_video_id}/hqdefault.jpg`
                  : undefined,
                embedUrl: t.youtube_video_id
                  ? `https://www.youtube.com/embed/${t.youtube_video_id}`
                  : undefined,
                publisher: {
                  "@type": "Organization",
                  name: "Beit Tefilah Rechovot",
                },
              }),
            },
          ]
        : undefined,
    };
  },
  component: TeachingDetail,
});

function TeachingDetail() {
  const { id } = useParams({ from: "/_authenticated/ensinos/$id" });
  const fn = useServerFn(getTeaching);
  const getProgressFn = useServerFn(getWatchProgress);
  const saveProgressFn = useServerFn(upsertWatchProgress);
  const profileFn = useServerFn(getMyProfile);
  const loaded = Route.useLoaderData();
  const { data, isLoading, error } = useQuery({
    queryKey: ["teaching", id],
    queryFn: () => fn({ data: { id } }),
    initialData: (loaded ?? undefined) as any,
  });

  const { data: progress, isPending: progressPending } = useQuery({
    queryKey: ["watch-progress", id],
    queryFn: () => getProgressFn({ data: { teachingId: id } }),
  });

  // Preferências pessoais de reprodução (autoplay / continuar de onde parou)
  const { data: myProfile } = useQuery({ queryKey: ["my-profile"], queryFn: () => profileFn() });
  const prefsReady = myProfile !== undefined;
  const autoplay = !!(myProfile as any)?.pref_autoplay;
  const resumePlayback = (myProfile as any)?.pref_resume_playback ?? true;

  // YouTube IFrame API para rastrear progresso
  const playerHostId = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const iframeRef = useRef<HTMLDivElement | null>(null);
  // A posição inicial é lida por referência: incluí-la nas dependências
  // recriaria o player quando a query de progresso resolvesse, reiniciando o
  // vídeo do começo no meio da reprodução.
  const startAtRef = useRef(0);
  startAtRef.current = resumePlayback ? (progress?.position_seconds ?? 0) : 0;
  // Só cria o player depois que a posição salva chegar, senão começaria do zero.
  const progressReady = !resumePlayback || !progressPending;
  // Instância viva do player + controle para retomar apenas uma vez por ensino.
  const playerRef = useRef<any>(null);
  const playerReadyRef = useRef(false);
  const resumedRef = useRef(false);

  useEffect(() => {
    playerRef.current = null;
    playerReadyRef.current = false;
    resumedRef.current = false;
  }, [id]);

  useEffect(() => {
    if (!data?.teaching?.youtube_video_id || !prefsReady || !progressReady) return;
    const videoId = data.teaching.youtube_video_id;
    const startAt = startAtRef.current;

    let player: any = null;
    let interval: number | undefined;
    let cancelled = false;

    const init = () => {
      if (cancelled) return;
      const YT = (window as any).YT;
      if (!YT || !YT.Player) return;
      player = new YT.Player(playerHostId, {
        videoId,
        playerVars: { start: startAt, rel: 0, modestbranding: 1, autoplay: autoplay ? 1 : 0 },

        events: {
          onReady: () => {
            playerRef.current = player;
            playerReadyRef.current = true;
            if (startAt > 3) resumedRef.current = true;
            interval = window.setInterval(() => {
              try {
                const pos = Math.floor(player.getCurrentTime?.() ?? 0);
                const dur = Math.floor(player.getDuration?.() ?? 0);
                if (pos > 3 && dur > 0) {
                  saveProgressFn({ data: { teachingId: id, position: pos, duration: dur } }).catch(
                    () => {},
                  );
                }
              } catch {
                // ignora falhas do player do YouTube
              }
            }, 15000);
          },
        },
      });
    };

    if (!(window as any).YT) {
      const SRC = "https://www.youtube.com/iframe_api";
      // Evita anexar o script mais de uma vez ao navegar entre ensinos.
      if (!document.querySelector(`script[src="${SRC}"]`)) {
        const tag = document.createElement("script");
        tag.src = SRC;
        document.body.appendChild(tag);
      }
      const previous = (window as any).onYouTubeIframeAPIReady;
      (window as any).onYouTubeIframeAPIReady = () => {
        if (typeof previous === "function") previous();
        init();
      };
    } else {
      init();
    }

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
      try {
        const pos = Math.floor(player?.getCurrentTime?.() ?? 0);
        const dur = Math.floor(player?.getDuration?.() ?? 0);
        if (pos > 3 && dur > 0) {
          saveProgressFn({ data: { teachingId: id, position: pos, duration: dur } }).catch(
            () => {},
          );
        }
        if (playerRef.current === player) {
          playerRef.current = null;
          playerReadyRef.current = false;
        }
        player?.destroy?.();
      } catch {
        // ignora falhas do player do YouTube
      }
    };
  }, [
    data?.teaching?.youtube_video_id,
    prefsReady,
    progressReady,
    autoplay,
    playerHostId,
    id,
    saveProgressFn,
  ]);

  // Se a posição salva chegar depois do player já existir, retoma via seekTo.
  const savedPosition = resumePlayback ? (progress?.position_seconds ?? 0) : 0;
  useEffect(() => {
    if (!resumePlayback || savedPosition <= 3 || resumedRef.current) return;
    let cancelled = false;
    const trySeek = () => {
      if (cancelled || resumedRef.current) return true;
      const player = playerRef.current;
      if (!player || !playerReadyRef.current) return false;
      try {
        const current = Math.floor(player.getCurrentTime?.() ?? 0);
        if (current <= 3) player.seekTo?.(savedPosition, true);
        resumedRef.current = true;
        return true;
      } catch {
        return false;
      }
    };
    if (trySeek()) return;
    const timer = window.setInterval(() => {
      if (trySeek()) window.clearInterval(timer);
    }, 300);
    const stop = window.setTimeout(() => window.clearInterval(timer), 15000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.clearTimeout(stop);
    };
  }, [savedPosition, resumePlayback, id]);

  if (isLoading) return <div className="h-96 animate-pulse rounded-xl bg-secondary" />;
  if (error || !data) return <p className="text-destructive">Erro ao carregar o ensino.</p>;

  const t = data.teaching;

  return (
    <article className="space-y-8">
      <Link
        to="/ensinos"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
      >
        <ArrowLeft className="h-4 w-4" /> Voltar ao catálogo
      </Link>

      <div className="grid gap-8 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="aspect-video overflow-hidden rounded-xl bg-black shadow-md">
            <div id={playerHostId} ref={iframeRef} className="h-full w-full" />
          </div>

          <header className="space-y-3">
            {t.parashiot && (
              <p className="text-sm font-medium uppercase tracking-wide text-gold">
                {t.parashiot.number}ª Porção · {t.parashiot.name_translit} · {t.parashiot.name_pt}
              </p>
            )}
            <div className="flex flex-wrap items-start justify-between gap-3">
              <h1 className="font-serif text-3xl font-semibold text-primary md:text-4xl">
                {t.custom_title?.trim() || t.title}
              </h1>
              <FavoriteButton teachingId={t.id} variant="full" />
            </div>
          </header>

          {t.description && (
            <div className="whitespace-pre-wrap rounded-xl border border-border bg-card p-6 text-sm leading-relaxed text-foreground/90">
              {t.description}
            </div>
          )}
        </div>

        <aside className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-5">
            <h2 className="font-serif text-lg font-semibold text-primary">Informações</h2>
            <dl className="mt-4 space-y-3 text-sm">
              <MetaRow icon={Calendar} label="Data">
                {t.teaching_date
                  ? formatDateBR(t.teaching_date, {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    })
                  : "—"}
              </MetaRow>
              {t.hebrew_date && (
                <MetaRow icon={Calendar} label="Calendário hebraico">
                  {t.hebrew_date}
                </MetaRow>
              )}
              {t.turno && (
                <MetaRow icon={t.turno === "manha" ? Sun : Moon} label="Turno">
                  {t.turno === "manha" ? "Manhã (11h)" : "Tarde (15h30)"}
                </MetaRow>
              )}

              {t.festivities?.name_pt && (
                <MetaRow icon={Book} label="Festividade">
                  {t.festivities.name_pt}
                </MetaRow>
              )}
            </dl>
          </div>

          {(t.torah_ref || t.haftarah_ref || t.brit_ref || t.psalm_ref) && (
            <div className="rounded-xl border border-border bg-card p-5">
              <h2 className="font-serif text-lg font-semibold text-primary">Referências</h2>
              <dl className="mt-4 space-y-2 text-sm">
                {t.torah_ref && (
                  <div>
                    <dt className="font-medium">Torá</dt>
                    <dd className="text-muted-foreground">{t.torah_ref}</dd>
                  </div>
                )}
                {t.haftarah_ref && (
                  <div>
                    <dt className="font-medium">Haftará</dt>
                    <dd className="text-muted-foreground">{t.haftarah_ref}</dd>
                  </div>
                )}
                {t.brit_ref && (
                  <div>
                    <dt className="font-medium">Brit Chadashá</dt>
                    <dd className="text-muted-foreground">{t.brit_ref}</dd>
                  </div>
                )}
                {t.psalm_ref && (
                  <div>
                    <dt className="font-medium">Salmos</dt>
                    <dd className="text-muted-foreground">{t.psalm_ref}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </aside>
      </div>

      <CommentsSection teachingId={t.id} />

      {data.related.length > 0 && (
        <section className="space-y-4">
          <h2 className="font-serif text-2xl font-semibold text-primary">
            Outros ensinos desta Parashá
          </h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {data.related.map((r: any) => (
              <TeachingCard key={r.id} teaching={r} />
            ))}
          </div>
        </section>
      )}
    </article>
  );
}

function CommentsSection({ teachingId }: { teachingId: string }) {
  const qc = useQueryClient();
  const listFn = useServerFn(listTeachingComments);
  const createFn = useServerFn(createTeachingComment);
  const deleteFn = useServerFn(deleteTeachingComment);
  const [text, setText] = useState("");
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setCurrentUserId(data.user?.id ?? null));
  }, []);

  const { data: comments, isLoading } = useQuery({
    queryKey: ["comments", teachingId],
    queryFn: () => listFn({ data: { teachingId } }),
  });

  const create = useMutation({
    mutationFn: () => createFn({ data: { teachingId, content: text.trim() } }),
    onSuccess: () => {
      setText("");
      toast.success("Comentário enviado! Ficará visível após aprovação de um administrador.");
      qc.invalidateQueries({ queryKey: ["comments", teachingId] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Não foi possível enviar o comentário."),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["comments", teachingId] }),
    onError: (e: any) => toast.error(e?.message ?? "Erro ao remover."),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (text.trim().length === 0) return;
    create.mutate();
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <MessageCircle className="h-5 w-5 text-primary" />
        <h2 className="font-serif text-2xl font-semibold text-primary">
          Comentários{" "}
          {comments ? (
            <span className="text-base font-normal text-muted-foreground">({comments.length})</span>
          ) : null}
        </h2>
      </div>

      <form onSubmit={submit} className="space-y-3 rounded-xl border border-border bg-card p-4">
        <Textarea
          placeholder="Compartilhe uma reflexão edificante sobre este ensino..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={2000}
          rows={3}
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{text.length}/2000</span>
          <Button type="submit" disabled={create.isPending || text.trim().length === 0}>
            {create.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enviando
              </>
            ) : (
              "Publicar comentário"
            )}
          </Button>
        </div>
      </form>

      <div className="space-y-3">
        {isLoading && <p className="text-sm text-muted-foreground">Carregando comentários...</p>}
        {comments && comments.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Nenhum comentário ainda. Seja o primeiro a comentar.
          </p>
        )}
        {comments?.map((c) => (
          <article key={c.id} className="flex gap-3 rounded-xl border border-border bg-card p-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-secondary text-sm font-semibold text-primary">
              {c.avatarUrl ? (
                <img src={c.avatarUrl} alt={c.firstName} className="h-full w-full object-cover" />
              ) : (
                <span>{c.firstName.slice(0, 1).toUpperCase()}</span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <header className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="flex items-baseline gap-2">
                  <span className="font-medium text-foreground">{c.firstName}</span>
                  <time className="text-xs text-muted-foreground" dateTime={c.createdAt}>
                    {formatDateTimeBR(c.createdAt, {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </time>
                  {c.status === "pending" && (
                    <span className="rounded-full bg-gold/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gold">
                      Aguardando aprovação
                    </span>
                  )}
                </div>
                {currentUserId === c.userId && (
                  <button
                    type="button"
                    onClick={() => remove.mutate(c.id)}
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive"
                    aria-label="Remover comentário"
                  >
                    <Trash2 className="h-3 w-3" /> remover
                  </button>
                )}
              </header>
              <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
                {c.content}
              </p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function MetaRow({
  icon: Icon,
  label,
  children,
}: {
  icon: any;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div>
        <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
        <dd className="font-medium text-foreground">{children}</dd>
      </div>
    </div>
  );
}
